<template>
  <div class="bg-white rounded-lg p-6 shadow-sm border border-gray-200 slide-in-up">
    <!-- 问题标题 -->
    <div class="mb-6">
      <h3 class="text-xl font-semibold text-gray-900 mb-2 text-wrap break-all">
        {{ question.title }}
      </h3>
      <p v-if="question.description" class="text-gray-600 text-wrap break-all">
        {{ question.description }}
      </p>
      <div v-if="question.is_required" class="flex items-center gap-1 mt-2">
        <span class="text-red-500 text-sm">*</span>
        <span class="text-sm text-gray-500">Required</span>
      </div>
    </div>

    <!-- 单选题 -->
    <div v-if="question.type === 'single_choice'" class="space-y-3">
      <div
        v-for="option in question.options"
        :key="option.value"
        class="relative"
      >
        <label class="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:border-primary hover:bg-primary-light/10"
               :class="{ 'border-primary bg-primary-light/20': answer === option.value }">
          <input
            type="radio"
            :value="option.value"
            :checked="answer === option.value"
            @change="handleSingleChoice(option.value)"
            class="sr-only"
          >
          <div class="flex items-center justify-between w-full">
            <span class="text-gray-900 font-medium">{{ option.label }}</span>
            <div class="flex items-center">
              <div class="w-5 h-5 border-2 border-gray-300 rounded-full flex items-center justify-center transition-colors duration-200"
                   :class="{ 'border-primary bg-primary': answer === option.value }">
                <div v-if="answer === option.value" class="w-2 h-2 bg-white rounded-full"></div>
              </div>
            </div>
          </div>
        </label>
      </div>
    </div>

    <!-- 多选题 -->
    <div v-else-if="question.type === 'multiple_choice'" class="space-y-3">
      <div
        v-for="option in question.options"
        :key="option.value"
        class="relative"
      >
        <label class="flex items-center p-4 border border-gray-200 rounded-lg cursor-pointer transition-all duration-200 hover:border-primary hover:bg-primary-light/10"
               :class="{ 'border-primary bg-primary-light/20': isOptionSelected(option.value) }">
          <input
            type="checkbox"
            :value="option.value"
            :checked="isOptionSelected(option.value)"
            @change="handleMultipleChoice(option.value, $event)"
            class="sr-only"
          >
          <div class="flex items-center justify-between w-full">
            <span class="text-gray-900 font-medium">{{ option.label }}</span>
            <div class="flex items-center">
              <div class="w-5 h-5 border-2 border-gray-300 rounded flex items-center justify-center transition-colors duration-200"
                   :class="{ 'border-primary bg-primary': isOptionSelected(option.value) }">
                <svg v-if="isOptionSelected(option.value)" class="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                </svg>
              </div>
            </div>
          </div>
        </label>
      </div>
    </div>

    <!-- 数字输入 -->
    <div v-else-if="question.type === 'number_input'" class="space-y-4">
      <div class="relative">
        <input
          type="number"
          :value="answer || ''"
          @input="handleNumberInput($event)"
          :placeholder="question.unit ? `Enter amount in ${question.unit}` : 'Enter a number'"
          class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 text-lg"
          min="0"
          step="0.1"
        >
        <div v-if="question.unit" class="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 font-medium">
          {{ question.unit }}
        </div>
      </div>
      
      <!-- 数字输入提示 -->
      <div class="flex items-center gap-2 text-sm text-gray-500">
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>Enter your best estimate. You can always update this later.</span>
      </div>
    </div>

    <!-- 文本输入 -->
    <div v-else-if="question.type === 'text_input'" class="space-y-4">
      <textarea
        :value="String(answer || '')"
        @input="handleTextInput($event)"
        placeholder="Enter your answer..."
        class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-200 resize-none"
        rows="4"
      ></textarea>
    </div>

    <!-- 答案预览 -->
    <div v-if="answer !== undefined && answer !== null && answer !== ''" class="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
      <div class="flex items-center gap-2 text-green-800">
        <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
          <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
        </svg>
        <span class="font-medium">Answer recorded</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">

import type { Question, QuestionAnswer } from '@/stores/questionnaire'

interface Props {
  question: Question
  answer?: QuestionAnswer
}

interface Emits {
  (e: 'answer', questionId: number, answer: QuestionAnswer): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

// 处理单选
const handleSingleChoice = (value: string) => {
  emit('answer', props.question.id, value)
}

// 处理多选
const handleMultipleChoice = (value: string, event: Event) => {
  const target = event.target as HTMLInputElement
  const currentAnswer = Array.isArray(props.answer) ? [...props.answer] : []
  
  if (target.checked) {
    if (!currentAnswer.includes(value)) {
      currentAnswer.push(value)
    }
  } else {
    const index = currentAnswer.indexOf(value)
    if (index > -1) {
      currentAnswer.splice(index, 1)
    }
  }
  
  emit('answer', props.question.id, currentAnswer)
}

// 检查选项是否被选中（多选）
const isOptionSelected = (value: string): boolean => {
  if (!Array.isArray(props.answer)) return false
  return props.answer.includes(value)
}

// 处理数字输入
const handleNumberInput = (event: Event) => {
  const target = event.target as HTMLInputElement
  const value = target.value ? parseFloat(target.value) : null
  emit('answer', props.question.id, value as QuestionAnswer)
}

// 处理文本输入
const handleTextInput = (event: Event) => {
  const target = event.target as HTMLTextAreaElement
  emit('answer', props.question.id, target.value)
}
</script>